/**
 * Auth App Entry Point
 *
 * This is a standalone React app for authentication pages that doesn't
 * require Bootstrap CSS. Django provides the page context via
 * window.__AUTH_CONTEXT__ and this app renders the appropriate page.
 *
 * Note: Signup is handled via invitation only - there is no public signup page.
 */
import { useEffect } from "react";
import { createRoot } from "react-dom/client";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { theme } from "./theme";
import { useAuthContext } from "./hooks/useAuthContext";
import { LoginPage } from "./pages/LoginPage";
import { PasswordResetPage } from "./pages/PasswordResetPage";
import {
  PasswordResetConfirmPage,
  PasswordChangeDonePage,
} from "./pages/PasswordResetConfirmPage";
import { PasswordResetDonePage } from "./pages/PasswordResetDonePage";
import { NoTeamAccessPage } from "./pages/NoTeamAccessPage";
import { SocialLoginConfirmPage } from "./pages/SocialLoginConfirmPage";
import { MfaIndexPage } from "./pages/MfaIndexPage";
import { TotpActivatePage, TotpDeactivatePage } from "./pages/TotpPages";
import {
  RecoveryCodesGeneratePage,
  RecoveryCodesPage,
} from "./pages/RecoveryCodesPages";
import { MfaAuthenticatePage } from "./pages/MfaAuthenticatePage";
import { ReauthenticatePage } from "./pages/ReauthenticatePage";
import { gettext } from "@services/i18n";

// Note: Montserrat font is loaded via Google Fonts in auth_base.html template

// Page titles for each auth page type
const PAGE_TITLES: Record<string, string> = {
  login: gettext("Sign In"),
  password_reset: gettext("Password Reset"),
  password_reset_done: gettext("Password Reset Email Sent"),
  password_reset_confirm: gettext("Change Password"),
  password_change_done: gettext("Password Changed"),
  no_team_access: gettext("Team Access Required"),
  social_login_confirm: gettext("Continue with Provider"),
  mfa_index: gettext("Two-factor authentication"),
  mfa_totp_activate: gettext("Activate authenticator app"),
  mfa_totp_deactivate: gettext("Deactivate authenticator app"),
  mfa_recovery_codes: gettext("Recovery codes"),
  mfa_recovery_codes_generate: gettext("Generate new recovery codes"),
  mfa_authenticate: gettext("Two-factor authentication"),
  reauthenticate: gettext("Confirm access"),
};

function AuthApp() {
  const { pageType } = useAuthContext();

  // Set document title based on page type
  useEffect(() => {
    const pageTitle = PAGE_TITLES[pageType] || PAGE_TITLES.login;
    document.title = `${pageTitle} - Captured Knowledge`;
  }, [pageType]);

  switch (pageType) {
    case "login":
      return <LoginPage />;
    case "password_reset":
      return <PasswordResetPage />;
    case "password_reset_confirm":
      return <PasswordResetConfirmPage />;
    case "password_reset_done":
      return <PasswordResetDonePage />;
    case "password_change_done":
      return <PasswordChangeDonePage />;
    case "no_team_access":
      return <NoTeamAccessPage />;
    case "social_login_confirm":
      return <SocialLoginConfirmPage />;
    case "mfa_index":
      return <MfaIndexPage />;
    case "mfa_totp_activate":
      return <TotpActivatePage />;
    case "mfa_totp_deactivate":
      return <TotpDeactivatePage />;
    case "mfa_recovery_codes":
      return <RecoveryCodesPage />;
    case "mfa_recovery_codes_generate":
      return <RecoveryCodesGeneratePage />;
    case "mfa_authenticate":
      return <MfaAuthenticatePage />;
    case "reauthenticate":
      return <ReauthenticatePage />;
    default:
      // Fallback to login
      return <LoginPage />;
  }
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthApp />
    </ThemeProvider>
  );
}

// Mount the app
const container = document.getElementById("auth-root");
if (container) {
  const root = createRoot(container);
  root.render(<App />);
}
